import {gql} from '@apollo/client'
import React, {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {IconButton} from '../../components/library/buttons'
import {NoResults} from '../../components/library/list'
import styled from 'styled-components'
import {Loader} from '../../components/loader'
import {EnvironmentNode} from '../../environment'
import {displayDate} from '../../lib/helpers'
import {cardTemplateVersionName} from '../card-builder/helpers'
import {ErrorInfo} from './error'
import {CardFilterFieldsBase} from '../../common/cards/synced-card-feed-filter-query'
import {
  CardCancelTaskResultFragment,
  useCardCancelProgressLazyQuery,
  useCardCancelTasksQuery
} from './generated/results'
import {FlowConfig} from '../../hooks/use-action-flow-config'

interface ResultContainerProps {
  environment: EnvironmentNode
  actionFlowConfigs: FlowConfig[]
}

export const ResultContainer = ({
  environment,
  actionFlowConfigs
}: ResultContainerProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | undefined>(
    undefined
  )
  /** Main task state */
  const {
    data: allTasksQueryResult,
    loading,
    called,
    refetch: refetchAllTasks
  } = useCardCancelTasksQuery({
    variables: {
      environmentId: environment.id
    }
  })
  const allTasks = useMemo(
    () => allTasksQueryResult?.cardCancelTasks?.nodes ?? [],
    [allTasksQueryResult?.cardCancelTasks?.nodes]
  )

  /** poll for tasks which need updating */
  const [doProgressQuery, {data: taskProgress, startPolling, stopPolling}] =
    useCardCancelProgressLazyQuery()

  useEffect(() => {
    const tasksToPoll = allTasks.filter(
      t => t.status === 'PENDING' || t.status === 'PROCESSING'
    )
    if (tasksToPoll.length)
      doProgressQuery({
        // bake in the latest params
        variables: {
          cancelInput: {
            environmentId: environment.id,
            organisationId: environment.organisationId,
            tasks: tasksToPoll.map(t => ({
              id: t.id,
              status: t.status,
              expectedCardCount: t.expectedCardCount ?? undefined
            }))
          }
        }
      }).then(() => startPolling(1000))
    return () => stopPolling()
  }, [
    allTasks,
    environment.id,
    environment.organisationId,
    doProgressQuery,
    startPolling,
    stopPolling
  ])

  const progressTasks = useMemo(
    () => taskProgress?.cardCancelTasksProgress.tasks ?? [],
    [taskProgress?.cardCancelTasksProgress.tasks]
  )

  const tasksWithLatestProgress = useMemo(
    () =>
      allTasks.map(task => {
        const newer = progressTasks.find(
          partialTask => partialTask?.id === task.id
        )
        if (newer) {
          return {
            ...task,
            status: newer.status,
            cancelledCardCount: newer.cancelledCardCount,
            expectedCardCount: newer.expectedCardCount
          } as CardCancelTaskResultFragment
        }
        return task
      }),
    [allTasks, progressTasks]
  )

  // check if any tasks have changed status
  // if so, stop polling and refetch the whole task list
  useEffect(() => {
    if (!taskProgress || !allTasksQueryResult) return
    const someStatusChanged = allTasksQueryResult.cardCancelTasks?.nodes.some(
      t => {
        const newer = taskProgress.cardCancelTasksProgress.tasks.find(
          pt => pt?.id === t.id
        )
        return newer && newer.status !== t.status
      }
    )
    if (someStatusChanged) {
      stopPolling()
      refetchAllTasks()
    }
  }, [taskProgress, allTasksQueryResult, stopPolling, refetchAllTasks])

  const isFinished = ({status}: CardCancelTaskResultFragment) =>
    status === 'COMPLETED' || status === 'FAILED'
  const inProgress = tasksWithLatestProgress.filter(r => !isFinished(r))
  const completed = tasksWithLatestProgress.filter(isFinished)
  const someInProgress = !!inProgress.length

  // poll to increment any in-progress results. note we also refetch the query
  // after creating a new task, in the parent component
  useEffect(() => {
    if (someInProgress) {
      startPolling(2000)
    } else {
      stopPolling()
    }
  }, [someInProgress, startPolling, stopPolling])

  if (!called || loading) {
    return <Loader />
  }

  return (
    <>
      {completed.length === 0 && inProgress.length === 0 && (
        <NoResults
          icon="regex"
          heading="No card cancellations yet"
          message="Card cancellation status will appear here"
        />
      )}

      {inProgress.map(result => (
        <InProgressResult
          key={result.id}
          environment={environment}
          result={result}
          actionFlowConfigs={actionFlowConfigs}
        />
      ))}

      {completed.map((result, i) => (
        <ResultWrap key={result.id}>
          <header onClick={() => setExpandedIndex(i)}>
            <h6>{displayDate(result.created)}</h6>
            {result.status === 'FAILED' ? (
              <ErrorInfo
                style={{
                  fontWeight: 'bold'
                }}
              >
                Cancellation failed
              </ErrorInfo>
            ) : (
              <h4>
                {result.cancelledCardCount.toLocaleString()} card
                {result.cancelledCardCount === 1 ? '' : 's'} cancelled
              </h4>
            )}
          </header>
          <ToggleWrap>
            <Toggle
              expanded={expandedIndex === i}
              setExpanded={expanded =>
                setExpandedIndex(expanded ? i : undefined)
              }
            />
          </ToggleWrap>

          {result.status === 'FAILED' ? (
            <ErrorDescription>
              {result.cancelledCardCount ? (
                <>
                  {' '}
                  {result.cancelledCardCount.toLocaleString()} of{' '}
                  {result.expectedCardCount?.toLocaleString()} cards cancelled.
                </>
              ) : null}{' '}
              {result.errors ? (
                <>
                  Cancellation could not be completed because:{' '}
                  {result.errors.join(', ')}
                </>
              ) : null}
            </ErrorDescription>
          ) : null}

          {expandedIndex === i && (
            <>
              <DisplayCriteria
                actionFlowConfigs={actionFlowConfigs}
                criteria={result.criteria as CardFilterFieldsBase}
              />
              Cancellation performed by
              {result.clientId ? (
                <Link to="../configuration/authentication-controls">
                  {' '}
                  API request credential:{' '}
                  <strong> {result.triggeredByDisplay}</strong>
                </Link>
              ) : (
                <>
                  :<strong> {result.triggeredByDisplay}</strong>
                </>
              )}
            </>
          )}
        </ResultWrap>
      ))}
    </>
  )
}

type InProgressResultProps = {
  environment: EnvironmentNode
  actionFlowConfigs: FlowConfig[]
  result: CardCancelTaskResultFragment
}
const InProgressResult = ({
  actionFlowConfigs,
  result
}: InProgressResultProps) => {
  const progress = result.expectedCardCount
    ? result.cancelledCardCount / result.expectedCardCount
    : 0
  return (
    <ResultWrap style={{marginBottom: '30px'}}>
      <header>
        <h4>
          {result.status === 'COMPLETED'
            ? 'Cancellation complete'
            : 'Cancellation in progress...'}
        </h4>
      </header>
      <p>
        {result.expectedCardCount ? (
          <>
            {result.expectedCardCount.toLocaleString()} card
            {result.expectedCardCount === 1 ? '' : 's'}
          </>
        ) : (
          'Preparing job'
        )}
      </p>
      <DisplayCriteria
        actionFlowConfigs={actionFlowConfigs}
        criteria={result.criteria as CardFilterFieldsBase}
      />
      Cancellation performed by:{' '}
      <strong>
        {result.clientId ? 'API request credential: ' : ''}
        {result.triggeredByDisplay}
      </strong>
      <Progress value={progress}>{progress}</Progress>
    </ResultWrap>
  )
}

type DisplayCriteriaProps = {
  criteria: CardFilterFieldsBase
  actionFlowConfigs: FlowConfig[]
}
const DisplayCriteria = ({
  criteria,
  actionFlowConfigs
}: DisplayCriteriaProps) => (
  <CriteriaList>
    {criteria.cardTemplateId && criteria.flowConfigId && (
      <li>
        Card template:{' '}
        {cardTemplateVersionName(
          actionFlowConfigs.find(c => c.id === criteria.flowConfigId)
            ?.currentVersion.config.cardTemplates?.[criteria.cardTemplateId]
            ?.metadata.cardName
        )}{' '}
        <span style={{opacity: 0.5}}>ID: {criteria.cardTemplateId}</span>
        <br />
      </li>
    )}

    {/* backwards compat for old jobs missing flowConfigId */}
    {criteria.cardTemplateId && !criteria.flowConfigId && (
      <li>
        Card template ID: {criteria.cardTemplateId}
        <br />
      </li>
    )}

    {criteria.flowConfigId && (
      <li>
        Action Flow:{' '}
        {actionFlowConfigs.find(c => c.id === criteria.flowConfigId)?.name}{' '}
        <span style={{opacity: 0.5}}>ID: {criteria.flowConfigId}</span>
        <br />
      </li>
    )}

    {criteria.cardTemplateVersion && (
      <li>
        Card template version: {criteria.cardTemplateVersion} <br />
      </li>
    )}

    {criteria.userId && (
      <li>
        Customer ID: {criteria.userId} <br />
      </li>
    )}

    {criteria.cardInstanceId && (
      <li>
        Card instance ID: {criteria.cardInstanceId} <br />
      </li>
    )}

    {criteria.flowInvocationId && (
      <li>
        Action Flow invocation ID: {criteria.flowInvocationId} <br />
      </li>
    )}

    {criteria.flowInstanceId && (
      <li>
        Action Flow instance ID: {criteria.flowInstanceId} <br />
      </li>
    )}
  </CriteriaList>
)

const Toggle = ({
  expanded,
  setExpanded
}: {
  expanded: boolean
  setExpanded: (v: boolean) => void
}) => (
  <IconButton
    size="large"
    icon="chevron"
    variant="muted"
    rotateIcon={expanded ? -90 : 90}
    onClick={e => {
      e.preventDefault()
      setExpanded(!expanded)
    }}
  />
)

const ToggleWrap = styled.div``

const ResultWrap = styled.div`
  border: 1px solid ${p => p.theme.grey10};
  border-radius: ${p => p.theme.borderRadius};
  padding: 20px;
  margin: 0 0 10px;
  position: relative;

  > header {
    margin: 0 0 15px;
    cursor: pointer;
    > h6 {
      color: ${p => p.theme.grey50};
      font-weight: normal;
      font-size: inherit;
      margin: 0 0 10px;
    }
    > h4 {
      margin: 0;
    }
  }

  ${ToggleWrap} {
    position: absolute;
    top: 0;
    right: 0;
    margin: 10px;
  }
`

const CriteriaList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    padding: 0;
    margin: 0 0 5px;
  }
`

const Progress = styled.progress`
  width: 100%;
  height: 20px;
  margin: 20px 0;
`

const ErrorDescription = styled.p`
  color: ${p => p.theme.red};
`

gql`
  fragment CardCancelTaskResult on CardCancelTask {
    id
    created
    status
    expectedCardCount
    cancelledCardCount
    triggeredByName
    triggeredByDisplay
    clientId
    criteria
    errors
  }

  query CardCancelTasks($environmentId: String!) {
    cardCancelTasks(
      condition: {environmentId: $environmentId}
      first: 20
      orderBy: CREATED_DESC
    ) {
      nodes {
        ...CardCancelTaskResult
      }
    }
  }

  # an optimised query which fetches directly from redis
  query cardCancelProgress($cancelInput: CardCancelProgressInput) {
    cardCancelTasksProgress(input: $cancelInput) {
      organisationId
      environmentId
      tasks {
        id
        status
        cancelledCardCount
        expectedCardCount
      }
    }
  }
`
